/** @jsx jsx */

import { Fragment } from 'react';

import { jsx } from '@emotion/core';
import { Heading, Margin, ProgressBar, Text } from '@zapier/design-system';

import LinkWithArrow from 'app/developer-v3/components/LinkWithArrow';
import DrillDownListItem from 'app/developer-v3/components/DrillDownListItem';
import { VersionStatus } from 'app/developer-v3/constants';
import { Check, usePrivatePlatform } from 'app/developer-v3/hooks';

import CheckSummary from './CheckSummary';
import PassingMessage from './PassingMessage';
import Styles from './Summary.styles';
import ValidatingLoader from './ValidatingLoader';
import { VALIDATIONS_DOCS_URL, SECTIONS } from './constants';

type Props = {
  changeSection: (section: SECTIONS) => void;
  checks: {
    activationTasks?: Check['activationTasks'];
    errors: Check['errors'];
    warnings: Check['warnings'];
  };
  hasValidated: boolean;
  isHelpVisible: boolean;
  isPassing: boolean;
  isPublished: boolean;
  isValidating: boolean;
  passingCount: number;
  totalChecks: number;
  versionStatus: VersionStatus;
};

export default function Summary({
  changeSection,
  checks,
  hasValidated,
  isHelpVisible,
  isPassing,
  isPublished,
  isValidating,
  passingCount,
  totalChecks,
  versionStatus,
}: Props) {
  const { isPublicIntentionElementVisible } = usePrivatePlatform();

  return (
    <section>
      <div css={Styles.description}>
        <PassingMessage show={hasValidated && isPassing} />
        <Text margin="10px 0px 25px 0px" tag="h2" type="SectionHeader">
          Validate integration
        </Text>
        {isHelpVisible && (
          <Margin margin="0px 0px 15px 0px">
            <Text margin="10px 0px 25px 0px" tag="p">
              {isPublicIntentionElementVisible
                ? 'Check your project for errors, get specific user experience recommendations, and get a list of remaining items that are required before you can make your integration available to everyone on Zapier.'
                : 'Check your project for errors and get specific user experience recommendations.'}
            </Text>
            <LinkWithArrow href={VALIDATIONS_DOCS_URL} iconName="arrowOffsite">
              Learn more about validating your integration
            </LinkWithArrow>
          </Margin>
        )}
      </div>
      <Heading>
        {isValidating ? (
          <ValidatingLoader label="Running validations..." />
        ) : (
          <Fragment>
            <div css={Styles.passingDisplay}>
              <title css={Styles.title} title="passing validations">
                <Text type="Title">validated</Text>
                <Text type="Title">
                  {passingCount}/{totalChecks}
                </Text>
              </title>
              <ProgressBar
                color="StatusSuccessWeaker"
                max={totalChecks}
                min={0}
                value={passingCount}
              />
            </div>
            <ul
              aria-label="failed check groups"
              css={Styles.summaryList}
              title={isPassing ? 'all passing' : 'to-do'}
            >
              <Heading>
                {Object.entries(checks).map(([checkType, check]) => {
                  return checkType === SECTIONS.ACTIVATION_TASKS &&
                    !isPublicIntentionElementVisible ? null : (
                    <li
                      aria-label="failed checks"
                      css={Styles.summaryItem}
                      key={checkType}
                    >
                      <DrillDownListItem
                        onClick={() => changeSection(checkType as SECTIONS)}
                      >
                        <CheckSummary
                          checkType={checkType}
                          count={check.totalFailures}
                          hasLoaded={hasValidated}
                          isPublished={isPublished}
                          versionStatus={versionStatus}
                        />
                      </DrillDownListItem>
                    </li>
                  );
                })}
              </Heading>
            </ul>
          </Fragment>
        )}
      </Heading>
    </section>
  );
}

Summary.defaultProps = {
  isHelpVisible: true,
};
